import { Link, useSearchParams } from 'react-router-dom'
import { Check, Clock, Loader2, X } from 'lucide-react'
import { buttonVariants } from '@/components/ui/button'
import { formatCurrency } from '@/lib/format'
import { cn } from '@/lib/utils'
import {
  CheckoutShell,
  CheckoutStatusBadge,
} from '@/components/checkout/CheckoutShell'
import { useCheckoutSession } from '@/hooks/useCheckout'
import type { CheckoutSession } from '@/types'

// PUBLIC Stripe success_url landing. Auth-independent (visitor may not be
// logged in) so it lives OUTSIDE every ProtectedRoute. UX-only — the real
// payment status is settled by the checkout.session.completed webhook.
//
// Feedback #51: after a portal Stripe payment the paying client must see an
// explicit Success ("Paiement réussi") or Declined ("Paiement refusé") screen.
// A settled session that is not `paid` (declined / expired / unpaid) shows the
// declined screen; only a genuinely-still-pending session shows "processing".

type View = 'loading' | 'success' | 'declined' | 'processing'

export default function CheckoutSuccessPage() {
  const [searchParams] = useSearchParams()
  const sessionId = searchParams.get('session_id')

  const { data, isLoading, isError } = useCheckoutSession(sessionId)
  const session = data?.data ?? null

  const view = resolveView({ sessionId, isLoading, isError, session })
  const amount = formatAmount(session?.amount_total, session?.currency)

  return (
    <CheckoutShell>
      {view === 'loading' ? (
        <>
          <Loader2
            className='h-12 w-12 animate-spin text-primary'
            aria-hidden='true'
          />
          <h1 className='text-xl font-semibold'>Vérification du paiement…</h1>
        </>
      ) : view === 'success' ? (
        <>
          <CheckoutStatusBadge className='bg-green-100 text-green-600'>
            <Check className='h-8 w-8' aria-hidden='true' />
          </CheckoutStatusBadge>
          <h1 className='text-xl font-semibold'>Paiement réussi</h1>
          <p className='text-sm text-muted-foreground'>
            Merci. Votre paiement a bien été reçu.
          </p>
          {amount && (
            <p className='text-3xl font-extrabold tabular-nums'>{amount}</p>
          )}
          <p className='mt-2 text-sm text-muted-foreground'>
            Vous pouvez fermer cette page et revenir à l&apos;application
            TigerFlow.
          </p>
          {session?.payment_id && (
            <Link
              to={`/payments/${session.payment_id}`}
              className={cn(buttonVariants({ variant: 'outline' }), 'mt-4')}
            >
              Voir les détails du paiement
            </Link>
          )}
        </>
      ) : view === 'declined' ? (
        <>
          <CheckoutStatusBadge className='bg-red-100 text-red-600'>
            <X className='h-8 w-8' aria-hidden='true' />
          </CheckoutStatusBadge>
          <h1 className='text-xl font-semibold'>Paiement refusé</h1>
          <p className='text-sm text-muted-foreground'>
            Nous n&apos;avons pas pu traiter votre paiement. Veuillez vérifier
            et réessayer.
          </p>
        </>
      ) : (
        <>
          <CheckoutStatusBadge className='bg-amber-100 text-amber-600'>
            <Clock className='h-8 w-8' aria-hidden='true' />
          </CheckoutStatusBadge>
          <h1 className='text-xl font-semibold'>
            Paiement en cours de confirmation
          </h1>
          <p className='text-sm text-muted-foreground'>
            Nous avons bien reçu votre demande. Stripe confirme le paiement.
          </p>
          <p className='mt-2 text-sm text-muted-foreground'>
            Vous pouvez fermer cette page. L&apos;écran du technicien se met à
            jour automatiquement dès que Stripe confirme le paiement.
          </p>
        </>
      )}
    </CheckoutShell>
  )
}

// No session id -> generic success (never crash). API error -> "processing":
// the webhook remains the source of truth, so we never claim failure on a
// transport error. A settled session whose payment_status is a recognised
// non-paid terminal state (declined / unpaid / failed / expired) -> "declined".
function resolveView({
  sessionId,
  isLoading,
  isError,
  session,
}: {
  sessionId: string | null
  isLoading: boolean
  isError: boolean
  session: CheckoutSession | null
}): View {
  if (!sessionId) return 'success'
  if (isLoading) return 'loading'
  if (isError) return 'processing'

  const status = session?.payment_status ?? null
  if (status === 'paid') return 'success'
  // Settled-but-not-paid Stripe outcomes => the payment was declined / not
  // completed. Anything else (e.g. no_payment_required, null) stays processing.
  if (
    status === 'unpaid' ||
    status === 'failed' ||
    status === 'declined' ||
    status === 'expired' ||
    status === 'canceled' ||
    status === 'cancelled'
  ) {
    return 'declined'
  }
  return 'processing'
}

function formatAmount(
  amount?: number | null,
  currency?: string | null
): string | null {
  if (amount == null) return null
  if (currency) {
    try {
      return formatCurrency(amount, currency, 2)
    } catch {
      return formatCurrency(amount, currency, 2)
    }
  }
  return formatCurrency(amount, 'EUR', 2)
}
